import { useQuery } from '@tanstack/react-query';
import { fetchPublicStatus } from '@/lib/api';
import { StatusDot } from '@/components/shared/StatusDot';
import { Globe, CheckCircle, AlertTriangle, XCircle, Server } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_CONFIG = {
  OPERATIONAL: { label: 'All Systems Operational', color: 'var(--nexus-success)', icon: CheckCircle, bg: 'bg-green-500/10 border-green-500/20' },
  DEGRADED: { label: 'Minor Service Degradation', color: 'var(--nexus-warning)', icon: AlertTriangle, bg: 'bg-amber-500/10 border-amber-500/20' },
  PARTIAL_OUTAGE: { label: 'Partial System Outage', color: 'var(--nexus-warning)', icon: AlertTriangle, bg: 'bg-amber-500/10 border-amber-500/20' },
  MAJOR_OUTAGE: { label: 'Major System Outage', color: 'var(--nexus-danger)', icon: XCircle, bg: 'bg-red-500/10 border-red-500/20' },
};

export default function StatusPage() {
  const { data: status, isLoading } = useQuery({
    queryKey: ['public-status'],
    queryFn: fetchPublicStatus,
    refetchInterval: 30000,
  });

  if (isLoading) return <div className="w-full max-w-[800px] mx-auto"><div className="h-32 bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" /></div>;

  const overall = STATUS_CONFIG[status?.overall_status] || STATUS_CONFIG.OPERATIONAL;
  const OverallIcon = overall.icon;

  return (
    <div data-testid="status-page" className="w-full max-w-[800px] mx-auto">
      <div className="mb-4 text-center">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">System Status</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Auto-managed by HERALD agent</p>
      </div>

      {/* Overall Status Banner */}
      <div className={`nexus-card relative overflow-hidden p-6 mb-4 border ${overall.bg}`}>
        <div className="flex items-center justify-center gap-3">
          <OverallIcon size={28} style={{ color: overall.color }} />
          <span className="font-heading text-lg font-semibold" style={{ color: overall.color }}>{overall.label}</span>
        </div>
        <p className="text-center text-[10px] text-[var(--nexus-text-muted)] font-mono mt-2">
          Last updated: {status?.last_updated ? new Date(status.last_updated).toLocaleString() : 'N/A'}
        </p>
      </div>

      {/* Services */}
      <div className="nexus-card relative overflow-hidden mb-4">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Services ({(status?.services || []).length})</h2>
        </div>
        <div className="p-3 space-y-1.5">
          {(status?.services || []).map((svc, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <Server size={12} className="text-[var(--nexus-text-muted)]" />
                <span className="font-mono text-xs text-[var(--nexus-text-primary)]">{svc.name}</span>
                <span className="text-[10px] text-[var(--nexus-text-muted)]">{svc.type}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <StatusDot status={svc.status === 'OPERATIONAL' ? 'active' : 'warning'} pulse={svc.status !== 'OPERATIONAL'} size="sm" />
                <span className={`text-[10px] ${svc.status === 'OPERATIONAL' ? 'text-[var(--nexus-success)]' : 'text-[var(--nexus-warning)]'}`}>{svc.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Incidents */}
      {status?.active_incidents?.length > 0 && (
        <div className="nexus-card relative overflow-hidden">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Active Incidents ({status.active_incidents.length})</h2>
          </div>
          <div className="p-3 space-y-1.5">
            {status.active_incidents.map(inc => (
              <Link key={inc.id} to={`/incidents/${inc.id}`} className="flex items-center justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03]">
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded badge-${inc.priority?.toLowerCase()}`}>{inc.priority}</span>
                  <span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{inc.id}</span>
                  <span className="text-[10px] text-[var(--nexus-text-secondary)]">{inc.title}</span>
                </div>
                <span className="text-[10px] text-[var(--nexus-text-muted)]">{inc.status}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
